import DescriptionIcon from '@mui/icons-material/Description';
import React, { useState, useRef } from 'react';
import {
  Grid,
  Typography,
  Stack,
  Dialog,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import Download_Icon from '../../../assets/images/Task_File__Donwload_Icon.svg';
import Delete_Icon from '../../../assets/images/Task_File_Delete_Icon.svg';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import GridOnIcon from '@mui/icons-material/GridOn';
import { Loader } from '../../../sharedComponents/Loader/Loader';
import { getFileTypes } from '../utils/helpers';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import AudioFileIcon from '@mui/icons-material/AudioFile';
import CodeIcon from '@mui/icons-material/Code';

const Attachments = ({ files, onAddFile, onDeleteFile, loading, taskId, userId }) => {
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const fileInputRef = useRef(null);

  const handleAddIconClick = () => {
    fileInputRef.current.click();
  };

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setOpenImageDialog(true);
  };

  const handleDownloadFile = (e) => {
    const fileUrl = e.location;
    const fileName = e.fileName || 'downloaded-file';
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderFile = (file) => {
    if (!file?.location) return 'No Files';

    const fileType = getFileTypes(file.location);
    const fileName = file?.fileName?.split('.').slice(0, -1).join('.') || file?.fileName;

    // Video files
    if (fileType === 'video') {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ width: '100%' }}>
          <video
            controls
            style={{ width: '100%', borderRadius: '5px', flex: '1 1 200px' }}
          >
            <source src={file.location} type={file.type} />
            Your browser does not support the video tag.
          </video>
          <Typography sx={{ fontSize: '10px', textAlign: 'center', width: '100%' }}>
            {fileName}
          </Typography>
        </Stack>
      );
    }

    // Image files
    if (fileType === 'image') {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ width: '100%' }}>
          <img
            src={file.location}
            alt={file.fileName || 'Uploaded content'}
            style={{
              width: '100%',
              maxHeight: '100px',
              flex: '1 1 200px',
              cursor: 'pointer',
              borderRadius: '5px',
              objectFit: 'contain',
            }}
            onClick={() => handleImageClick(file.location)}
          />
          <Typography sx={{ fontSize: '10px', textAlign: 'center', width: '100%' }}>
            {fileName}
          </Typography>
        </Stack>
      );
    }

    // Audio files
    if (fileType === 'audio') {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100px' }}>
          <AudioFileIcon sx={{ fontSize: 60, color: '#7e7e7eff' }} />
          <audio controls style={{ width: '100%', marginTop: '8px' }}>
            <source src={file.location} type={file.type} />
            Your browser does not support the audio tag.
          </audio>
          <Typography sx={{ fontSize: '10px', textAlign: 'center', width: '100%' }}>
            {fileName}
          </Typography>
        </Stack>
      );
    }

    // Word documents
    if (
      fileType === 'docx' ||
      file?.fileName?.toLowerCase().endsWith('.docx') ||
      file?.fileName?.toLowerCase().endsWith('.doc')
    ) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100px' }}>
          <DescriptionIcon sx={{ fontSize: 60, color: '#7e7e7eff' }} />
          <Typography sx={{ fontSize: '10px', textAlign: 'center', width: '100%' }}>
            {fileName}
          </Typography>
        </Stack>
      );
    }

    // PDF files
    if (
      fileType === 'pdf' ||
      file?.fileName?.toLowerCase().endsWith('.pdf')
    ) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100px' }}>
          <PictureAsPdfIcon sx={{ fontSize: 60, color: '#7e7e7eff' }} />
          <Typography sx={{ fontSize: '10px', textAlign: 'center', width: '100%' }}>
            {fileName}
          </Typography>
        </Stack>
      );
    }

    // Excel files
    if (
      fileType === 'excel' ||
      file?.fileName?.toLowerCase().endsWith('.xlsx') ||
      file?.fileName?.toLowerCase().endsWith('.xls')
    ) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100px' }}>
          <GridOnIcon sx={{ fontSize: 60, color: '#7e7e7eff' }} />
          <Typography sx={{ fontSize: '10px', textAlign: 'center', width: '100%' }}>
            {fileName}
          </Typography>
        </Stack>
      );
    }

    // PowerPoint files
    if (
      fileType === 'powerpoint' ||
      file?.fileName?.toLowerCase().endsWith('.pptx') ||
      file?.fileName?.toLowerCase().endsWith('.ppt')
    ) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100px' }}>
          <DescriptionIcon sx={{ fontSize: 60, color: '#7e7e7eff' }} />
          <Typography sx={{ fontSize: '10px', textAlign: 'center', width: '100%' }}>
            {fileName}
          </Typography>
        </Stack>
      );
    }

    // Archive files
    if (
      fileType === 'archive' ||
      file?.fileName?.toLowerCase().endsWith('.zip') ||
      file?.fileName?.toLowerCase().endsWith('.rar') ||
      file?.fileName?.toLowerCase().endsWith('.tar') ||
      file?.fileName?.toLowerCase().endsWith('.gz')
    ) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100px' }}>
          <FolderZipIcon sx={{ fontSize: 60, color: '#7e7e7eff' }} />
          <Typography sx={{ fontSize: '10px', textAlign: 'center', width: '100%' }}>
            {fileName}
          </Typography>
        </Stack>
      );
    }

    // Code files
    if (
      fileType === 'code' ||
      file?.fileName?.toLowerCase().endsWith('.js') ||
      file?.fileName?.toLowerCase().endsWith('.jsx') ||
      file?.fileName?.toLowerCase().endsWith('.ts') ||
      file?.fileName?.toLowerCase().endsWith('.tsx') ||
      file?.fileName?.toLowerCase().endsWith('.html') ||
      file?.fileName?.toLowerCase().endsWith('.css') ||
      file?.fileName?.toLowerCase().endsWith('.py') ||
      file?.fileName?.toLowerCase().endsWith('.java') ||
      file?.fileName?.toLowerCase().endsWith('.cpp') ||
      file?.fileName?.toLowerCase().endsWith('.c') ||
      file?.fileName?.toLowerCase().endsWith('.php') ||
      file?.fileName?.toLowerCase().endsWith('.rb') ||
      file?.fileName?.toLowerCase().endsWith('.json') ||
      file?.fileName?.toLowerCase().endsWith('.xml')
    ) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100px' }}>
          <CodeIcon sx={{ fontSize: 60, color: '#7e7e7eff' }} />
          <Typography sx={{ fontSize: '10px', textAlign: 'center', width: '100%' }}>
            {fileName}
          </Typography>
        </Stack>
      );
    }

    // Text files
    if (
      fileType === 'text' ||
      file?.fileName?.toLowerCase().endsWith('.txt') ||
      file?.fileName?.toLowerCase().endsWith('.rtf') ||
      file?.fileName?.toLowerCase().endsWith('.md')
    ) {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100px' }}>
          <DescriptionIcon sx={{ fontSize: 60, color: '#7e7e7eff' }} />
          <Typography sx={{ fontSize: '10px', textAlign: 'center', width: '100%' }}>
            {fileName}
          </Typography>
        </Stack>
      );
    }

    // Default file icon for any other file type
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100px' }}>
        <InsertDriveFileIcon sx={{ fontSize: 60, color: '#7e7e7eff' }} />
        <Typography sx={{ fontSize: '10px', textAlign: 'center', width: '100%' }}>
          {fileName}
        </Typography>
      </Stack>
    );
  };

  return (
    <>
      <Grid
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 3,
        }}
      >
        <Typography
          sx={{ fontSize: '16px', color: '#181818', fontWeight: 700 }}
        >
          Attachments
        </Typography>
        <Typography sx={{ marginRight: '5%' }}>
          <AddIcon
            onClick={handleAddIconClick}
            sx={{ cursor: 'pointer' }}
          />
          <input
            type="file"
            hidden
            ref={fileInputRef}
            onChange={(e) => {
              const selectedFile = e.target.files[0];
              if (selectedFile) {
                onAddFile(taskId, selectedFile);
              }
            }}
          />
        </Typography>
      </Grid>
      <Grid
        container
        item
        xs={12}
        mt={1}
        spacing={1}
        sx={{ paddingLeft: '1.5%' }}
      >
        {files?.length > 0
          ? files?.map((file, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Stack
                sx={{
                  border: '1px solid #E4E4E7',
                  height: '100%',
                  width: '100%',
                  borderRadius: '5px',
                }}
              >
                <div style={{ position: 'relative' }}>
                  {renderFile(file)}
                  <img
                    src={Download_Icon}
                    alt="Download_Icon"
                    style={{
                      position: 'absolute',
                      top: 10,
                      right: 40,
                      cursor: 'pointer',
                      background: 'white',
                      borderRadius: '5px',
                      padding: '2px',
                    }}
                    onClick={() => handleDownloadFile(file)}
                  />
                  <img
                    src={Delete_Icon}
                    alt="Delete_Icon"
                    style={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      cursor: 'pointer',
                      background: 'white',
                      borderRadius: '5px',
                      padding: '2px',
                    }}
                    onClick={() => onDeleteFile(file)}
                  />
                </div>
              </Stack>
            </Grid>
          ))
          : "No Files"}
        {loading && (
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'center'}
            sx={{ width: '80px', height: '100px' }}
          >
            <Loader loading={loading} color={'#008080'}></Loader>
          </Stack>
        )}
      </Grid>
      <Dialog
        open={openImageDialog}
        onClose={() => setOpenImageDialog(false)}
        maxWidth="md"
      >
        <img src={selectedImage} alt="Selected" style={{ width: '100%' }} />
      </Dialog>
    </>
  );
};

export default Attachments;
